import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Volume2, 
  Music, 
  Zap, 
  Store, 
  Sparkles, 
  Globe, 
  Shield, 
  Headphones,
  ExternalLink,
  Mail,
  Building2
} from 'lucide-react';

const LandingPage = () => {
  // Shopify App Store URL
  const appStoreUrl = 'https://apps.shopify.com/speakify';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-indigo-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-indigo-600 p-2 rounded-lg">
                <Volume2 className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Speakify</h1>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#features" className="text-gray-600 hover:text-indigo-600 transition-colors">Features</a>
              <a href="#benefits" className="text-gray-600 hover:text-indigo-600 transition-colors">Benefits</a>
              <a href="#about" className="text-gray-600 hover:text-indigo-600 transition-colors">About</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4 mr-2" />
            AI-Powered Audio for Shopify
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Add <span className="text-indigo-600">Voice</span> to Your Products
          </h2>
          
          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            Transform your Shopify store with AI-powered audio descriptions. 
            Let your products speak to customers with premium text-to-speech technology 
            and custom voice cloning.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-4 text-lg transition-all duration-200 transform hover:scale-105"
              onClick={() => window.open(appStoreUrl, '_blank')}
            >
              <Store className="h-5 w-5 mr-2" />
              Install from Shopify App Store
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </div>
          
          <p className="mt-4 text-sm text-gray-500">
            Available exclusively on the Shopify App Store
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Powerful Features</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to add engaging audio experiences to your products
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-indigo-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Music className="h-7 w-7 text-indigo-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Premium AI Voices</h4>
                <p className="text-gray-600">
                  Choose from multiple high-quality AI voices powered by ElevenLabs to match your brand personality.
                </p>
              </CardContent>
            </Card>
            
            {/* Feature 2 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-green-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Headphones className="h-7 w-7 text-green-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Custom Voice Cloning</h4>
                <p className="text-gray-600">
                  Clone your own voice or create a unique brand voice using our instant voice cloning technology.
                </p>
              </CardContent>
            </Card>
            
            {/* Feature 3 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-purple-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="h-7 w-7 text-purple-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">AI Text Enhancement</h4>
                <p className="text-gray-600">
                  Enhance your product descriptions with Claude AI before converting to audio for maximum impact.
                </p>
              </CardContent>
            </Card>
            
            {/* Feature 4 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-orange-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="h-7 w-7 text-orange-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">One-Click Generation</h4>
                <p className="text-gray-600">
                  Auto-generate audio from your existing product descriptions with a single click.
                </p>
              </CardContent>
            </Card>
            
            {/* Feature 5 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-blue-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Store className="h-7 w-7 text-blue-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Seamless Integration</h4>
                <p className="text-gray-600">
                  Native Shopify Theme App Extension that works with any Online Store 2.0 theme.
                </p>
              </CardContent>
            </Card>
            
            {/* Feature 6 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-red-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="h-7 w-7 text-red-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Multi-Language Support</h4>
                <p className="text-gray-600">
                  Create audio in multiple languages to reach your global customer base.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-indigo-100">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Why Add Audio to Your Products?
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Increase Engagement</h4>
                    <p className="text-gray-600">Audio content keeps customers on your product pages longer, increasing the chance of conversion.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Improve Accessibility</h4>
                    <p className="text-gray-600">Make your store accessible to visually impaired customers and those who prefer audio content.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Stand Out from Competition</h4>
                    <p className="text-gray-600">Differentiate your brand with unique audio experiences that competitors do not offer.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Higher Conversion Rates</h4>
                    <p className="text-gray-600">Multi-sensory product presentations lead to better understanding and more purchases.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Enhanced Mobile Experience</h4>
                    <p className="text-gray-600">Audio is perfect for mobile shoppers who want to learn about products on the go.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Build Brand Identity</h4>
                    <p className="text-gray-600">Create a consistent brand voice across all your products with custom voice cloning.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-indigo-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Give Your Products a Voice?
          </h3>
          <p className="text-indigo-100 text-lg mb-8">
            Join merchants who are transforming their customer experience with audio
          </p>
          <Button 
            size="lg"
            variant="secondary"
            className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-8 py-4 text-lg"
            onClick={() => window.open(appStoreUrl, '_blank')}
          >
            Get Started on Shopify
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer id="about" className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="bg-indigo-600 p-2 rounded-lg">
                  <Volume2 className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold">Speakify</h3>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                Transform your e-commerce experience with AI-powered audio descriptions. 
                Make your products speak to customers.
              </p>
              <div className="flex items-center space-x-2 text-gray-400">
                <Shield className="h-4 w-4" />
                <span className="text-sm">GDPR Compliant</span>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4 text-gray-300">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link 
                    to="/privacy-policy" 
                    className="text-gray-400 hover:text-indigo-400 text-sm transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/terms-of-service" 
                    className="text-gray-400 hover:text-indigo-400 text-sm transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-gray-300">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="mailto:question2abhi@gmail.com" 
                    className="text-gray-400 hover:text-indigo-400 text-sm transition-colors flex items-center space-x-2"
                  >
                    <Mail className="h-4 w-4" />
                    <span>question2abhi@gmail.com</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Built by Vortex AI Advisory */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-2 rounded-lg">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Built with ❤️ by</p>
                  <Link 
                    to="/vortex-ai-advisory" 
                    className="text-white font-semibold hover:text-indigo-400 transition-colors"
                  >
                    Vortex AI Advisory
                  </Link>
                </div>
              </div>
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} Speakify. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
